import { Component, Input, SimpleChanges } from '@angular/core';
import { PracticeChartComponent } from './practice-chart/practice-chart.component';
import { PracticeTableComponent } from './practice-table/practice-table.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { CommonModule } from '@angular/common';
import { QaiLoadingComponent } from '@qai/ui';
import { AssignmentCategory, IPracticeAssignmentStatsRequest } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-request';
import { IPracticeAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-response';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { AssignmentFilterComponent, DEFAULT_STATUS_FILTER } from '../assignment-filter/assignment-filter.component';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { filterAssignmentsByStatus } from 'src/app/utils/reporting-dashboard.util';


@Component({
  selector: 'app-practice-tab',
  templateUrl: './practice-tab.component.html',
  styleUrls: ['./practice-tab.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    PracticeChartComponent,
    PracticeTableComponent,
    TranslocoModule,
    CommonModule,
    QaiLoadingComponent,
    AssignmentFilterComponent
  ],
})
export class PracticeTabComponent {
  @Input() selectedAccount: { label: string; value: string } | null = null;
  @Input() selectedTeams: string[] = [];
  @Input() tabActive: boolean = false;

  isTranslationLoaded: boolean = false;
  isLoading: boolean = true;
  isNetworkCallInprogress: boolean = false;
  useStatusFilter: boolean = false;
  practiceAssignmentStats: IPracticeAssignmentStatsResponse[] = [];
  visiblePracticeAssignments: IPracticeAssignmentStatsResponse[] = [];
  currentFilter: AssignmentCategory = 'RECENT_ASSIGNMENTS';

  allPracticeAssignmentStats: IPracticeAssignmentStatsResponse[] = [];
  selectedStatuses: string[] = [];
  syncedStatuses: string[] | null = null;
  private statsLoaded: boolean = false;

  constructor(
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isTranslationLoaded && (changes['selectedAccount'] || changes['selectedTeams'])) {
      this.statsLoaded = false;
      this.loadPracticeAssignmentStats();
    }
    if (changes['tabActive'] && this.tabActive && !changes['tabActive'].firstChange) {
      const stored = ReportingLocalStorageUtil.getStoredRollupStatusFilter() ?? [...DEFAULT_STATUS_FILTER];
      this.selectedStatuses = stored;
      this.syncedStatuses = [...stored];
      if (this.statsLoaded) {
        this.applyStatusFilter();
      }
    }
  }

  async ngOnInit() {
    this.useStatusFilter = await this.featureFlagsService.isFlagEnabled(
      AppConstants.REPORTING_ASSIGNMENT_STATUS_FILTER
    );
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.isLoading = false;
    });
  }

  onVisibleAssignmentsChanged(visibleAssignments: IPracticeAssignmentStatsResponse[]): void {
    this.visiblePracticeAssignments = visibleAssignments;
  }

  onAssignmentChange(event: string): void {
    this.currentFilter = event as AssignmentCategory;
    this.loadPracticeAssignmentStats();
  }

  // Status filter handler (flag ON)
  onStatusFilterChange(statuses: string[]): void {
    this.selectedStatuses = statuses;
    if (!this.statsLoaded) {
      this.loadPracticeAssignmentStats();
    } else {
      this.applyStatusFilter();
    }
  }

  private applyStatusFilter(): void {
    this.practiceAssignmentStats = filterAssignmentsByStatus(this.allPracticeAssignmentStats, this.selectedStatuses);
    this.visiblePracticeAssignments = [];
  }

  loadPracticeAssignmentStats() {
    if (!this.selectedAccount?.value) {
      console.warn('No account selected');
      return;
    }

    this.isNetworkCallInprogress = true;

    const teamUuids = this.selectedTeams.includes('all')
      ? []
      : this.selectedTeams;

    const payload: IPracticeAssignmentStatsRequest = {
      accountUuid: this.selectedAccount.value,
      teamUuids: teamUuids,
      category: this.useStatusFilter ? 'ALL_ASSIGNMENTS' : this.currentFilter,
    };

    this.reportingDashboardService
      .getPracticeAssignmentStats(payload, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .subscribe({
        next: (response) => {
          this.isNetworkCallInprogress = false;
          this.statsLoaded = true;
          if (this.useStatusFilter) {
            this.allPracticeAssignmentStats = response;
            this.applyStatusFilter();
          } else {
            this.practiceAssignmentStats = response;
          }
          console.log('Practice stats loaded:', response);
        },
        error: (error) => {
          console.error('Error loading practice stats:', error);
          this.isNetworkCallInprogress = false;
          this.statsLoaded = true;
          this.practiceAssignmentStats = [];
          this.allPracticeAssignmentStats = [];
          this.visiblePracticeAssignments = [];
        },
      });
  }
}